import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import LogoIcon from "../../assets/images/logo/logo-icon.jpg";
import countryData from "../../components/Languages.json";
import { setInitial } from "../../store/slices/chatSlice";
import { useDispatch } from "react-redux";
import UploadService from "../../services/pdf-upload";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import { CircleX } from "lucide-react";

const DocumentUpload = () => {
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [intervalId, setIntervalId] = useState(null);

  const formik = useFormik({
    initialValues: {
      language: "",
      file: null,
    },
    validationSchema: Yup.object({
      language: Yup.string().required("esto es requerido"),
      file: Yup.mixed()
        .required("esto es requerido")
        .test(
          "fileFormat",
          "Please select .ppt, .pptx, .doc, .docx, .txt,.pdf",
          (value) =>
            value &&
            [
              "application/pdf",
              "text/plain",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              "application/vnd.ms-powerpoint",
              "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            ].includes(value.type)
        ),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", values?.file);
      try {
        const response = await UploadService?.uploadFile(formData, values?.language);
        if (response?.data?.response?.task_id) {
          const id = response?.data?.response?.task_id;
          if (intervalId) clearInterval(intervalId);
          const newIntervalId = setInterval(async () => {
            try {
              const payload = { id };
              const status = await UploadService?.status(payload);
              if (status?.data?.status === "completed") {
                clearInterval(newIntervalId);
                setIntervalId(null);
                await getResult(status?.data?.task_id);
                setLoading(false);
                formik.resetForm();
              } else if (["failed", "error", "cancelled"].includes(status)) {
                clearInterval(newIntervalId);
                setIntervalId(null);
                toastError("Algo salió mal");
              }
            } catch (error) {
              clearInterval(newIntervalId);
              setIntervalId(null);
              toastError("Algo salió mal");
            }
          }, 2000);
          setIntervalId(newIntervalId);
        }
      } catch (error) {
        toastError(error?.response?.data?.message || "Algo salió mal");
        setDownloadUrl(null);
        setLoading(false);
      }
    },
  });

  const getResult = async (id) => {
    const payload = { id };
    try {
      const response = await UploadService?.result(payload);
      if (response?.data?.fileUrl) {
        const dlUrl = response?.data?.fileUrl;
        try {
          const fetchResponse = await fetch(dlUrl);
          if (!fetchResponse.ok) throw new Error("Fetch failed");
          const blob = await fetchResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const filename = formik.values.file?.name
            ? formik.values.file.name.replace(/\.[^/.]+$/, "") + ".docx"
            : "Documento traducido.docx";
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }, 100);
        } catch {
          toastError("Error al descargar el archivo. Por favor inténtalo de nuevo.");
        }
      } else {
        toastError("No se encontró la URL de descarga en la respuesta.");
      }
    } catch (serviceError) {
      toastError(serviceError?.response?.data?.message || "Algo salió mal");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen(true);

  const handleCountrySelect = (item) => {
    formik.setFieldValue("language", item.name);
    setIsOpen(false);
    setSearchTerm("");
  };

  const filteredLanguages = countryData.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) formik.setFieldValue("file", file);
    e.target.value = null;
  };

  useEffect(() => {
    dispatch(setInitial(true));
  }, []);

  return (
    <div className="flex flex-col w-full h-full p-4 md:p-6 lg:p-8 overflow-y-auto">
      {/* Page header */}
      <div className="mb-6 flex-shrink-0">
        <h1 className="font-roboto font-bold text-xl lg:text-2xl text-primary uppercase tracking-wide">
          Organizations
        </h1>
        <p className="font-roboto text-sm text-gray-500 mt-0.5">
          Translate and manage organization documents
        </p>
      </div>

      {/* Centered card */}
      <div className="flex flex-1 items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm border border-primary/10 w-full max-w-xl p-4 sm:p-6 lg:p-10">
          <form className="grid gap-6" onSubmit={formik.handleSubmit}>
            {/* Logo + heading */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="h-12 w-12 flex-shrink-0">
                <img src={LogoIcon} alt="Logo" className="w-full h-full rounded-full object-cover" />
              </div>
              <h2 className="font-roboto font-semibold text-lg lg:text-xl text-primary text-center sm:text-left">
                ¡Perfecto! ¿Qué archivo deseas traducir?
              </h2>
            </div>

            {/* Language selector */}
            <div ref={dropdownRef} className="relative">
              <label className="font-roboto text-xs font-bold text-[#42445A] uppercase tracking-wide block mb-2">
                Target Language
              </label>
              <div
                className="cursor-pointer w-full rounded-xl px-4 py-3 bg-[#F0EEF8] border border-primary/20 flex items-center justify-between hover:border-primary transition-colors"
                onClick={toggleDropdown}
              >
                <p className="font-roboto font-semibold text-primary text-sm uppercase">
                  {formik.values.language || "Select Target Language"}
                </p>
                <svg className={`h-4 w-4 text-primary transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {isOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="z-50 absolute left-0 mt-2 w-full rounded-xl bg-white border border-primary/20 py-2 overflow-hidden shadow-lg"
                >
                  <div className="px-3 pb-2">
                    <input
                      type="text"
                      placeholder="Search language..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full px-3 py-2 text-sm border border-primary/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 font-roboto bg-[#F0EEF8]"
                    />
                  </div>
                  <div className="max-h-44 overflow-y-auto dropdown-scroll">
                    {filteredLanguages.length > 0 ? (
                      filteredLanguages.map((item, index) => (
                        <button
                          key={index}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F0EEF8] hover:text-primary w-full text-left font-roboto transition-colors"
                          onClick={() => handleCountrySelect(item)}
                          type="button"
                        >
                          {item.name}
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-3 text-sm text-gray-500 text-center font-roboto">
                        No language found
                      </p>
                    )}
                  </div>
                </div>
              )}
              {formik.errors.language && formik.touched.language && (
                <p className="text-red-500 text-xs mt-1">{formik.errors.language}</p>
              )}
            </div>

            {/* File upload */}
            <div className="relative">
              <label className="font-roboto text-xs font-bold text-[#42445A] uppercase tracking-wide block mb-2">
                Document
              </label>
              <input
                type="file"
                id="upload"
                hidden
                accept=".ppt,.pptx,.doc,.docx,.txt,.pdf"
                onChange={handleFileUpload}
                disabled={loading}
              />
              <label
                htmlFor="upload"
                className="bg-[#F0EEF8] border-2 border-dashed border-primary/30 rounded-xl p-6 sm:p-8 lg:p-10 w-full flex flex-col justify-center items-center text-center cursor-pointer hover:border-primary hover:bg-[#E8E4F5] transition-all gap-3"
              >
                {formik.values.file ? (
                  <span className="font-roboto font-bold text-primary text-sm uppercase">
                    {formik.values.file?.name}
                  </span>
                ) : (
                  <>
                    <svg className="h-10 w-10 text-primary/40 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <p className="font-roboto font-bold text-primary uppercase text-sm tracking-wide">
                      Select File
                    </p>
                    <p className="font-roboto text-xs text-gray-400 uppercase tracking-wide">
                      .PPT | .PPTX | .DOC | .DOCX | .TXT | .PDF
                    </p>
                  </>
                )}
              </label>
              {formik.values.file && (
                <button
                  type="button"
                  disabled={loading}
                  className="absolute top-10 right-3 text-gray-400 hover:text-red-500 transition-colors"
                  onClick={() => formik.setFieldValue("file", null)}
                >
                  <CircleX size={20} />
                </button>
              )}
              {formik.errors.file && formik.touched.file && (
                <p className="text-red-500 text-xs mt-1">{formik.errors.file}</p>
              )}
            </div>

            {/* Submit */}
            <div className="flex justify-center mt-2">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center justify-center h-[44px] px-8 sm:px-12 rounded-full bg-primary text-white font-roboto font-bold text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors disabled:opacity-70 disabled:cursor-not-allowed min-w-[140px] sm:min-w-[160px]"
              >
                {loading ? <div className="loader flex justify-center items-center mx-8" /> : "TRANSLATE"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;
